import { createContext, useContext, type ReactNode } from "react";
import { AuthStore } from "#shared/store/auth-store.tsx";
import { anonymousApi } from "#login/lib/anonymous-api.ts";
import { TokenType } from "@gain/common-enum";
import { persistToken } from "#shared/lib/token-utils.ts";
import {
  redirectToApp,
  consumeAuthReturnUrl,
} from "#shared/lib/router-utils.ts";
import {
  persistAdminConsoleRoute,
  removeAdminConsoleRoute,
} from "#shared/lib/admin-console-route.ts";

// eslint-disable-next-line react-refresh/only-export-components
export class AuthLoginStore extends AuthStore {
  protected getContextId(): string {
    throw new Error("Method not needed for login context.");
  }
  constructor() {
    super(TokenType.Master);
  }

  async login(
    params:
      | {
          type: "password" | "passwordless";
          email: string;
          password?: string;
          newPassword?: string;
        }
      | { type: "otp"; otp: string; newPassword?: string },
    options?: { deferRedirect?: boolean },
  ): Promise<string> {
    const body =
      params.type === "otp"
        ? { otp: params.otp, newPassword: params.newPassword }
        : {
            type: params.type,
            email: params.email,
            password: params.password ?? "",
            newPassword: params.newPassword,
          };

    const response = await anonymousApi.auth.login(
      { body },
      { fetchOptions: { credentials: "include" } },
    );

    persistToken(TokenType.Master, "global", response.token);

    if (response.redirectTo === "/") {
      removeAdminConsoleRoute();
    } else {
      persistAdminConsoleRoute(response.redirectTo);
    }

    const redirectTo = consumeAuthReturnUrl() ?? response.redirectTo;
    if (!options?.deferRedirect) redirectToApp(redirectTo);
    return redirectTo;
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export const authLoginStore = new AuthLoginStore();
const Context = createContext<AuthLoginStore | null>(null);

export function AuthLoginProvider({ children }: { children: ReactNode }) {
  authLoginStore.useSync();

  return <Context.Provider value={authLoginStore}>{children}</Context.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthLoginContext(): AuthLoginStore {
  const ctx = useContext(Context);
  if (!ctx)
    throw new Error(
      "useAuthLoginContext must be used inside <AuthLoginProvider>",
    );
  ctx.useSync();
  return ctx;
}
